/**
 * Vendedores (SalesRep) — ABM.
 * Dos escenarios: con acceso al sistema (usuario vinculado, escenario 1) o de
 * solo atribución sin licencia (escenario 2).
 */
import { useState } from 'react'
import { Plus, Pencil, UserCheck, UserX, Loader2, Search, Percent, Trash2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import {
  useSalesReps, useCreateSalesRep, useUpdateSalesRep,
  useCommissionRules, useCreateCommissionRule, useDeleteCommissionRule,
  type SalesRep, type SalesRepInput,
} from '@/modules/sales/hooks/useSalesReps'
import { useProducts } from '@/modules/sales/hooks/useSales'
import { useTenantUsers } from '@/modules/users/hooks/useUsers'
import { useAuthStore } from '@/store/authStore'
import { inputCls } from '@/lib/formStyles'
import { extractFieldErrors, extractErrorMessage } from '@/lib/errorUtils'
import { usePermission } from '@/hooks/usePermission'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { Modal } from '@/components/ds/Modal'

function RepModal({ rep, onClose }: { rep: SalesRep | null; onClose: () => void }) {
  const isEdit = !!rep
  const create = useCreateSalesRep()
  const update = useUpdateSalesRep()
  const { data: usersData } = useTenantUsers()
  const [form, setForm] = useState<SalesRepInput>({
    name: rep?.name ?? '', tax_id: rep?.tax_id ?? '', email: rep?.email ?? '',
    phone: rep?.phone ?? '',
    commission_rate: rep?.commission_rate ?? '',
    is_active: rep?.is_active ?? true,
    user: rep?.user ?? null,
  })
  const [error, setError] = useState<string>('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const pending = create.isPending || update.isPending
  const members = usersData?.users ?? []

  const submit = () => {
    setError(''); setFieldErrors({})
    const payload: SalesRepInput = {
      ...form,
      commission_rate: form.commission_rate === '' ? null : form.commission_rate,
      user: form.user || null,
    }
    const onErr = (e: unknown) => {
      setFieldErrors(extractFieldErrors(e) ?? {})
      setError(extractErrorMessage(e, 'No se pudo guardar el vendedor.'))
    }
    if (isEdit) {
      update.mutate({ id: rep!.id, ...payload }, { onSuccess: onClose, onError: onErr })
    } else {
      create.mutate(payload, { onSuccess: onClose, onError: onErr })
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar vendedor' : 'Nuevo vendedor'} size="lg"
      actions={<>
        <button onClick={onClose} className="rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">Cancelar</button>
        <button onClick={submit} disabled={pending} className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
          {pending && <Loader2 size={14} className="animate-spin" />} Guardar
        </button>
      </>}
    >
      <div className="max-h-[70vh] space-y-3 overflow-y-auto">
        {error && <div className="rounded-lg bg-danger-surface px-3 py-2 text-sm text-danger-content">{error}</div>}
        <div>
          <label htmlFor="salesrepspage-name" className="mb-1 block text-xs font-medium text-content-muted">Nombre *</label>
          <input id="salesrepspage-name" value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} />
          {fieldErrors.name && <p className="mt-0.5 text-xs text-danger-solid">{fieldErrors.name}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="salesrepspage-tax-id" className="mb-1 block text-xs font-medium text-content-muted">RUT / ID</label>
            <input id="salesrepspage-tax-id" value={form.tax_id ?? ''} onChange={e => setForm(f => ({ ...f, tax_id: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label htmlFor="salesrepspage-commission-rate" className="mb-1 block text-xs font-medium text-content-muted">Comisión por defecto (%)</label>
            <input id="salesrepspage-commission-rate" value={form.commission_rate ?? ''} onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))} className={inputCls} placeholder="0.00" />
          </div>
        </div>
        <p className="-mt-1 text-xs text-content-subtle">
          La comisión por defecto rige en los productos <strong>afectos a comisión</strong> que no
          tengan un trato específico para este vendedor. Vacío = usa la tasa de cada producto.
          Cero = no comisiona.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="salesrepspage-email" className="mb-1 block text-xs font-medium text-content-muted">Email</label>
            <input id="salesrepspage-email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label htmlFor="salesrepspage-phone" className="mb-1 block text-xs font-medium text-content-muted">Teléfono</label>
            <input id="salesrepspage-phone" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inputCls} />
          </div>
        </div>
        <div>
          <label htmlFor="salesrepspage-acceso-al-sistema-opcional" className="mb-1 block text-xs font-medium text-content-muted">Acceso al sistema (opcional)</label>
          <select id="salesrepspage-acceso-al-sistema-opcional" value={(form.user as string) ?? ''} onChange={e => setForm(f => ({ ...f, user: e.target.value || null }))} className={inputCls}>
            <option value="">Solo atribución (sin acceso ni licencia)</option>
            {members.map((u: any) => (
              <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
            ))}
          </select>
          {fieldErrors.user && <p className="mt-0.5 text-xs text-danger-solid">{fieldErrors.user}</p>}
          <p className="mt-0.5 text-xs text-content-subtle">
            Vincula un usuario del tenant para darle acceso (escenario 1). Debe estar invitado en Usuarios.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          Activo
        </label>
      </div>
    </Modal>
  )
}

/**
 * Matriz vendedor × producto: el trato particular, que gana sobre la comisión por
 * defecto del vendedor y sobre la del producto. Solo se ofrecen productos AFECTOS
 * a comisión: en uno no afecto la fila no haría nada.
 */
function CommissionRulesPanel({ rep, onClose }: { rep: SalesRep; onClose: () => void }) {
  const { data: rules, isLoading } = useCommissionRules({ sales_rep: rep.id })
  const { data: productsData } = useProducts({ page_size: 200 })
  const create = useCreateCommissionRule()
  const remove = useDeleteCommissionRule()
  const [productId, setProductId] = useState('')
  const [rate, setRate] = useState('')
  const [error, setError] = useState('')

  const commissionable = (productsData?.products ?? []).filter(p => p.is_commissionable)
  const yaPactados = new Set((rules ?? []).map(r => r.product))
  const disponibles = commissionable.filter(p => !yaPactados.has(p.id))

  const add = () => {
    setError('')
    if (!productId || rate === '') { setError('Elegir un producto e indicar el porcentaje.'); return }
    create.mutate(
      { sales_rep: rep.id, product: productId, commission_rate: rate },
      {
        onSuccess: () => { setProductId(''); setRate('') },
        onError: e => setError(extractErrorMessage(e, 'No se pudo guardar el trato.')),
      },
    )
  }

  const fallback = rep.commission_rate
    ? `${rep.commission_rate}% (comisión por defecto de ${rep.name})`
    : 'la tasa de cada producto'

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Comisiones por producto — ${rep.name}`}
      subtitle={`Los productos sin trato acá usan ${fallback}.`}
      actions={
        <>
          <button onClick={onClose} className="rounded-lg border border-divider px-3 py-1.5 text-sm text-content-muted hover:bg-soft">
            Cerrar
          </button>
        </>
      }
    >
      <div>
        {error && <div className="mb-3 rounded-lg bg-danger-surface px-3 py-2 text-sm text-danger-content">{error}</div>}

        {isLoading ? (
          <Loader2 className="mx-auto animate-spin text-content-subtle" />
        ) : (rules ?? []).length === 0 ? (
          <p className="py-4 text-center text-sm text-content-subtle">
            Sin tratos específicos. {rep.name} usa {fallback}.
          </p>
        ) : (
          <DataTable<NonNullable<typeof rules>[number]>
            columns={[
              { key: 'product_name', header: 'Producto',
                render: r => (
                  <span className="text-content">
                    {r.product_name}
                    <span className="ml-1 text-xs text-content-subtle">{r.product_sku}</span>
                    {/* 🔴 El aviso va acá y no en un tooltip: un trato sobre un
                        producto no afecto NO comisiona, y sin decirlo la fila se
                        lee como que la regla está rigiendo. */}
                    {!r.product_is_commissionable && (
                      <span className="ml-2 rounded-full bg-warning-surface px-2 py-0.5 text-xs text-warning-content">
                        El producto no es afecto: no comisiona
                      </span>
                    )}
                  </span>
                ) },
              { key: 'commission_rate', header: 'Comisión',
                render: r => (
                  <span className="text-content-muted">
                    {r.commission_rate}% {r.commission_base === 'margin' ? 'sobre margen' : 'sobre total'}
                  </span>
                ) },
              { key: 'acciones', header: '', width: 56,
                render: r => (
                  <div className="text-right">
                    <button
                      onClick={() => remove.mutate(r.id)}
                      className="rounded p-1 text-content-subtle hover:bg-soft hover:text-danger-content-soft"
                      title="Eliminar trato"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ) },
            ]}
            rows={rules ?? []}
            rowKey={r => r.id}
            minWidth={420}
            surface={false}
            caption="Tratos de comisión por producto de este vendedor"
          />
        )}

        <div className="mt-4 rounded-lg border border-divider bg-glass-3 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-content-subtle">Agregar trato</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[240px] flex-1">
              <label htmlFor="salesrepspage-producto" className="mb-1 block text-xs font-medium text-content-muted">Producto</label>
              <select id="salesrepspage-producto" value={productId} onChange={e => setProductId(e.target.value)} className={inputCls}>
                <option value="">Selecciona un producto…</option>
                {disponibles.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                ))}
              </select>
            </div>
            <div className="w-32">
              <label htmlFor="salesrepspage-comision" className="mb-1 block text-xs font-medium text-content-muted">Comisión (%)</label>
              <input id="salesrepspage-comision" value={rate} onChange={e => setRate(e.target.value)} type="number" step="0.01"
                min="0" max="100" placeholder="0.00" className={inputCls} />
            </div>
            <button onClick={add} disabled={create.isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-on-primary hover:bg-primary-hover disabled:opacity-60">
              {create.isPending && <Loader2 size={14} className="animate-spin" />} Agregar
            </button>
          </div>
          {disponibles.length === 0 && commissionable.length > 0 && (
            <p className="mt-2 text-xs text-content-subtle">
              Ya hay un trato para cada producto afecto a comisión.
            </p>
          )}
          {commissionable.length === 0 && (
            <p className="mt-2 text-xs text-content-subtle">
              No hay productos marcados como afectos a comisión. Márcalos primero en Productos.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

export function SalesRepsPage() {
  const { can } = usePermission()
  const { activeTenant, user } = useAuthStore()
  // La matriz de comisiones es solo para administradores, igual que los campos
  // de comisión del producto (el backend la protege con IsTenantAdmin).
  const isAdmin = !!(user?.is_platform_admin || activeTenant?.roles?.includes('admin'))
  const canEdit = can('sales.customers', 'edit')
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const { data: reps, isLoading } = useSalesReps({
    active: showInactive ? 'all' : 'true',
    search: search || undefined,
  })
  const [modalRep, setModalRep] = useState<SalesRep | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [rulesRep, setRulesRep] = useState<SalesRep | null>(null)

  const openNew = () => { setModalRep(null); setModalOpen(true) }
  const openEdit = (r: SalesRep) => { setModalRep(r); setModalOpen(true) }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <PageHeader
        title="Vendedores"
        subtitle={`${(reps ?? []).length} ${(reps ?? []).length === 1 ? 'vendedor' : 'vendedores'}`}
        help={HELP['sales.sales_reps']}
      >
        {canEdit && (
          <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={16} /> Nuevo vendedor
          </button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-content-subtle" />
          <input aria-label="Buscar por nombre, RUT o email" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, RUT o email"
            className={`${inputCls} w-72 pl-8`} />
        </div>
        <label className="flex items-center gap-2 text-sm text-content-muted">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Ver inactivos
        </label>
      </div>

      <DataTable<NonNullable<typeof reps>[number]>
        columns={[
          { key: 'name', header: 'Nombre', sortable: true,
            render: r => <span className="font-medium text-content">{r.name}</span> },
          { key: 'tax_id', header: 'RUT / ID', sortable: true,
            render: r => <span className="text-content-muted">{r.tax_id || '—'}</span> },
          { key: 'email', header: 'Email', sortable: true,
            render: r => <span className="text-content-muted">{r.email || '—'}</span> },
          { key: 'commission_rate', header: 'Comisión', numeric: true, sortable: true,
            sortValue: r => Number(r.commission_rate ?? 0),
            render: r => (
              <span className="text-content-muted">{r.commission_rate ? `${r.commission_rate}%` : '—'}</span>
            ) },
          { key: 'has_system_access', header: 'Acceso', sortable: true,
            sortValue: r => (r.has_system_access ? 1 : 0),
            render: r => (r.has_system_access ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-success-surface px-2 py-0.5 text-xs text-success-content"><UserCheck size={12} /> Con acceso</span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-glass-3 px-2 py-0.5 text-xs text-content-muted"><UserX size={12} /> Solo atribución</span>
            )) },
          { key: 'is_active', header: 'Estado', sortable: true,
            sortValue: r => (r.is_active ? 1 : 0),
            render: r => (
              <span className={r.is_active ? 'text-success-content-soft' : 'text-content-subtle'}>
                {r.is_active ? 'Activo' : 'Inactivo'}
              </span>
            ) },
          { key: 'acciones', header: '', width: 90,
            render: r => (
              <div className="text-right">
                {isAdmin && (
                  <button onClick={() => setRulesRep(r)} className="rounded p-1 text-content-subtle hover:bg-soft hover:text-primary" title="Comisiones por producto">
                    <Percent size={15} />
                  </button>
                )}
                {canEdit && (
                  <button onClick={() => openEdit(r)} className="rounded p-1 text-content-subtle hover:bg-soft hover:text-primary" title="Editar">
                    <Pencil size={15} />
                  </button>
                )}
              </div>
            ) },
        ]}
        rows={reps ?? []}
        rowKey={r => r.id}
        minWidth={980}
        loading={isLoading}
        empty={<EmptyState title="Sin vendedores." size="sm" />}
        caption="Vendedores y su comisión por defecto"
      />

      {modalOpen && <RepModal rep={modalRep} onClose={() => setModalOpen(false)} />}
      {rulesRep && <CommissionRulesPanel rep={rulesRep} onClose={() => setRulesRep(null)} />}
    </div>
  )
}

export default SalesRepsPage
